import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import OfficeBuildingOutlineIcon from 'mdi-react/OfficeBuildingOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import FormatListBulletedIcon from 'mdi-react/FormatListBulletedIcon';
import FilterRemoveOutlineIcon from 'mdi-react/FilterRemoveOutlineIcon';
import ViewGridOutlineIcon from 'mdi-react/ViewGridOutlineIcon';
import type Office from '../models/office';
import type Parking from '../../parkings/models/parking';
import type Facility from '../../facilities/models/facility';
import OfficeCard from '../cards/office-card.component';
import ActivityIndicator
  from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center }
  from '../../../core/components/center/center.component';
import ListPager
  from '../../../core/components/list-pager/list-pager.component';
import { IsMobileContext }
  from '../../../core/context/is-mobile';
import RouteConstants from '../../../core/routes.constants';
import StyleConstants from '../../../core/styles.constants';
import * as listStyles from '../../../core/admin-list.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import { useSessionState } from '../../../core/hooks/session-state.hook';

const ALL_VISIBILITY = '__all__';
const VISIBILITY_VISIBLE = '__visible__';
const VISIBILITY_HIDDEN = '__hidden__';
// Whether the office has the floor plan option switched on.
const ALL_FLOOR_PLANS = '__all_floor_plans__';
const FLOOR_PLAN_ON = '__floor_plan_on__';
const FLOOR_PLAN_OFF = '__floor_plan_off__';
const ITEMS_PER_PAGE = 10;

/** Session-storage keys for this tab's toolbar. Namespaced by tab so the three do not share. */
const FILTER_KEYS = {
  search: 'my-office.offices.search',
  visibility: 'my-office.offices.visibility',
  floorPlan: 'my-office.offices.floor-plan',
  sort: 'my-office.offices.sort',
  view: 'my-office.offices.view',
};

type OfficeSort = 'name' | 'rooms' | 'users';
type OfficeView = 'list' | 'grid';

const SORT_OPTIONS: { value: OfficeSort; labelKey: string }[] = [
  { value: 'name', labelKey: 'sort-name' },
  { value: 'rooms', labelKey: 'sort-most-rooms' },
  { value: 'users', labelKey: 'sort-most-users' },
];

interface Props {
  readonly allOffices: Office[] | undefined;
  readonly allParkings: Parking[] | undefined;
  readonly allFacilities: Facility[] | undefined;
}

const OfficeOverviewGroup = ({
  allOffices,
  allParkings,
  allFacilities,
}: Props): React.JSX.Element => {
  const navigate = useNavigate();
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  /**
   * Loading is derived directly from the incoming `allOffices` prop rather than
   * being held as local state.  The previous pattern called `setIsLoading(false)`
   * inside the render function itself, which is a React anti-pattern: it forces
   * an additional synchronous re-render every time the data arrives, prolonging
   * the visible spinner by one extra paint cycle.
   *
   * With the derived approach the transition from "loading" to "loaded" happens
   * in the same render pass that delivers the data – no extra cycle needed.
   */
  const isLoading = allOffices == null;
  // Held in session storage so a reload, or a trip into an office and back, does not throw
  // away the filters the admin set up. Page is deliberately not persisted: the list it
  // indexes into can change between visits, and page 1 of a stale result is a worse
  // starting point than the top of the list.
  const [searchQuery, setSearchQuery] = useSessionState(FILTER_KEYS.search, '');
  const [visibilityFilter, setVisibilityFilter] = useSessionState(
    FILTER_KEYS.visibility,
    ALL_VISIBILITY,
  );
  const [floorPlanFilter, setFloorPlanFilter] = useSessionState(
    FILTER_KEYS.floorPlan,
    ALL_FLOOR_PLANS,
  );
  const [sortOrder, setSortOrder] = useSessionState<OfficeSort>(FILTER_KEYS.sort, 'name');
  const [view, setView] = useSessionState<OfficeView>(FILTER_KEYS.view, 'list');
  const [page, setPage] = useState(0);

  // Sort order and the list/grid switch are display preferences rather than filters: they
  // hide nothing, so clearing does not touch them.
  const hasActiveFilters = searchQuery.trim() !== ''
    || visibilityFilter !== ALL_VISIBILITY
    || floorPlanFilter !== ALL_FLOOR_PLANS;

  const clearFilters = useCallback((): void => {
    setSearchQuery('');
    setVisibilityFilter(ALL_VISIBILITY);
    setFloorPlanFilter(ALL_FLOOR_PLANS);
    setPage(0);
  }, [setSearchQuery, setVisibilityFilter, setFloorPlanFilter]);

  const sortedOffices = useMemo(
    () => [...(allOffices ?? [])].sort(
      (a, b) => (
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      ),
    ),
    [allOffices],
  );

  const filteredOffices = useMemo(() => {
    let result = sortedOffices;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (o) => o.name.toLowerCase().includes(q),
      );
    }
    if (visibilityFilter === VISIBILITY_VISIBLE) {
      result = result.filter((o) => o.isAvailableInApp);
    }
    if (visibilityFilter === VISIBILITY_HIDDEN) {
      result = result.filter((o) => !o.isAvailableInApp);
    }
    if (floorPlanFilter === FLOOR_PLAN_ON) {
      result = result.filter((o) => o.floorPlanEnabled);
    }
    if (floorPlanFilter === FLOOR_PLAN_OFF) {
      result = result.filter((o) => !o.floorPlanEnabled);
    }

    // `sortedOffices` is already name-ordered, so the other orders only need their own key with
    // the name as the tie-breaker.
    if (sortOrder === 'rooms') {
      result = [...result].sort((a, b) => (
        b.rooms.length - a.rooms.length
        || (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
      ));
    } else if (sortOrder === 'users') {
      result = [...result].sort((a, b) => (
        b.permittedUsers.length - a.permittedUsers.length
        || (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
      ));
    }

    return result;
  }, [sortedOffices, searchQuery, visibilityFilter, floorPlanFilter, sortOrder]);

  // Clamped because a stale page (after filtering or a deletion) would render an empty slice.
  const lastPage = Math.max(0, Math.ceil(filteredOffices.length / ITEMS_PER_PAGE) - 1);
  const currentPage = Math.min(page, lastPage);

  const paginatedOffices = filteredOffices.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const parkingsForOffice = useCallback(
    (officeId: string): number => (
      (allParkings ?? []).filter(
        (p) => p.officeIds.includes(officeId),
      ).length
    ),
    [allParkings],
  );

  const facilitiesForOffice = useCallback(
    (officeId: string): number => (
      (allFacilities ?? []).filter(
        (f) => f.officeIds.includes(officeId),
      ).length
    ),
    [allFacilities],
  );

  const navigateToCreate = useCallback(() => {
    navigate(
      `/${RouteConstants.PATH_OFFICES}${RouteConstants.PATH_CREATE}`,
    );
  }, [navigate]);

  const navigateToEdit = useCallback(
    (id: string) => {
      navigate(`/${RouteConstants.PATH_OFFICES}/${id}`);
    },
    [navigate],
  );

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleVisibilityChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    setVisibilityFilter(e.target.value);
    setPage(0);
  };

  const handleFloorPlanChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    setFloorPlanFilter(e.target.value);
    setPage(0);
  };

  const renderSectionHead = (): React.JSX.Element => (
    <div className={style(surfaceStyles.cardHead(isMobile))}>
      <div className={style(listStyles.panelHeadLeft)}>
        <div className={style(listStyles.sectionTile)}>
          <OfficeBuildingOutlineIcon size={20} />
        </div>
        <div>
          <h2 className={style(listStyles.panelTitle)}>
            {t('offices')}
            {!isLoading && (
              <span className={style(listStyles.countPill)}>
                {sortedOffices.length}
              </span>
            )}
          </h2>
        </div>
      </div>
      <button
        type="button"
        className={style(surfaceStyles.primaryButton)}
        onClick={navigateToCreate}
        disabled={isLoading}
      >
        <PlusIcon size={16} />
        {t('create-new-office')}
      </button>
    </div>
  );

  const renderToolbar = (): React.JSX.Element | null => {
    if (isLoading || sortedOffices.length === 0) return null;
    return (
      <div className={style(listStyles.toolbar(isMobile))}>
        <div className={style(surfaceStyles.searchWrap(isMobile))}>
          <span className={style(surfaceStyles.searchIcon)}>
            <MagnifyIcon size={16} />
          </span>
          <input
            type="text"
            className={style(surfaceStyles.searchInput)}
            placeholder={String(t('search-by-name'))}
            aria-label={String(t('search-by-name'))}
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <select
          className={style(listStyles.filterSelect(isMobile))}
          value={visibilityFilter}
          aria-label={String(t('all-visibility'))}
          onChange={handleVisibilityChange}
        >
          <option value={ALL_VISIBILITY}>{t('all-visibility')}</option>
          <option value={VISIBILITY_VISIBLE}>{t('visible-in-app')}</option>
          <option value={VISIBILITY_HIDDEN}>{t('hidden-in-app')}</option>
        </select>
        <select
          className={style(listStyles.filterSelect(isMobile))}
          value={floorPlanFilter}
          aria-label={String(t('all-floor-plans'))}
          onChange={handleFloorPlanChange}
        >
          <option value={ALL_FLOOR_PLANS}>{t('all-floor-plans')}</option>
          <option value={FLOOR_PLAN_ON}>{t('with-floor-plan')}</option>
          <option value={FLOOR_PLAN_OFF}>{t('without-floor-plan')}</option>
        </select>

        {/* Sort and the view switch sit at the far end of the toolbar. */}
        <select
          className={style(listStyles.sortSelect(isMobile))}
          value={sortOrder}
          aria-label={String(t('sort-by'))}
          onChange={(e): void => {
            const next = SORT_OPTIONS.find((option) => option.value === e.target.value);
            if (next != null) {
              setSortOrder(next.value);
              setPage(0);
            }
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>

        <div className={style(listStyles.viewToggle)} role="group" aria-label={String(t('view'))}>
          <button
            type="button"
            className={style(listStyles.viewToggleButton(view === 'list', true))}
            aria-pressed={view === 'list'}
            aria-label={String(t('list-view'))}
            onClick={(): void => setView('list')}
          >
            <FormatListBulletedIcon size={18} />
          </button>
          <button
            type="button"
            className={style(listStyles.viewToggleButton(view === 'grid', false))}
            aria-pressed={view === 'grid'}
            aria-label={String(t('grid-view'))}
            onClick={(): void => setView('grid')}
          >
            <ViewGridOutlineIcon size={18} />
          </button>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            className={style(surfaceStyles.ghostButton)}
            onClick={clearFilters}
            title={String(t('clear-filters'))}
          >
            <FilterRemoveOutlineIcon size={16} />
            {t('clear-filters')}
          </button>
        )}
      </div>
    );
  };

  const renderContent = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <div className={style(listStyles.loadingPad)}>
          <Center>
            <ActivityIndicator
              size={20}
              color={StyleConstants.GREY_COLOR}
            />
          </Center>
        </div>
      );
    }

    if (filteredOffices.length === 0) {
      const isEmpty = sortedOffices.length === 0;
      return (
        <div className={style(surfaceStyles.emptyState)}>
          <OfficeBuildingOutlineIcon size={28} />
          <span className={style(surfaceStyles.emptyTitle)}>
            {isEmpty ? t('no-offices-available') : t('no-results-found')}
          </span>
          <span className={style(surfaceStyles.emptyHint)}>
            {isEmpty
              ? t('no-offices-available-hint')
              : t('no-office-results-hint')}
          </span>
        </div>
      );
    }

    const cards = paginatedOffices.map((office) => (
      <OfficeCard
        key={office.id}
        id={office.id ?? ''}
        name={office.name}
        amountOfRooms={office.rooms.length}
        amountOfEmails={office.permittedUsers.length}
        isAvailableInApp={office.isAvailableInApp}
        floorPlanEnabled={office.floorPlanEnabled}
        linkedParkings={parkingsForOffice(office.id ?? '')}
        linkedFacilities={facilitiesForOffice(office.id ?? '')}
        variant={view === 'grid' ? 'grid' : 'row'}
        onClick={(): void => {
          navigateToEdit(office.id ?? '');
        }}
      />
    ));

    if (view === 'grid') {
      return <div className={style(listStyles.cardGrid(isMobile))}>{cards}</div>;
    }

    return <div>{cards}</div>;
  };

  return (
    <section className={style(surfaceStyles.card)}>
      {renderSectionHead()}
      {renderToolbar()}
      {renderContent()}
      {!isLoading && filteredOffices.length > 0 && (
        <div className={style(listStyles.sectionFoot)}>
          <ListPager
            page={currentPage}
            pageSize={ITEMS_PER_PAGE}
            total={filteredOffices.length}
            onChangePage={setPage}
          />
        </div>
      )}
    </section>
  );
};

export default OfficeOverviewGroup;
